// Admin Videos Tab — manage videos for the "le Prieuré s'éveille…" gallery
// Two ways to add: paste a YouTube/Vimeo URL (kind=embed) or upload a video file (kind=upload).
import { useCallback, useEffect, useRef, useState } from "react";
import api from "@/lib/api";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
const resolveAsset = (u) => (!u ? "" : u.startsWith("http") ? u : `${BACKEND}${u}`);

export default function VideosTab({ locale }) {
  const isFr = locale === "fr";
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [embedUrl, setEmbedUrl] = useState("");
  const [embedBusy, setEmbedBusy] = useState(false);
  const [uploadBusy, setUploadBusy] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null);
  const fileInputRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/videos");
      setItems(data.items || []);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const onAddEmbed = async (e) => {
    e.preventDefault();
    if (!embedUrl.trim()) return;
    setErr("");
    setEmbedBusy(true);
    try {
      const maxOrder = items.reduce((m, x) => Math.max(m, x.order || 0), 0);
      await api.post("/admin/videos", {
        url: embedUrl.trim(),
        kind: "embed",
        on_gallery: true,
        order: maxOrder + 1,
        caption: { fr: "", en: "" },
      });
      setEmbedUrl("");
      await load();
    } catch (e2) {
      setErr(e2?.response?.data?.detail || (isFr ? "Erreur d'ajout" : "Add failed"));
    } finally {
      setEmbedBusy(false);
    }
  };

  const onUpload = async (ev) => {
    const file = ev.target.files?.[0];
    if (!file) return;
    setErr("");
    setUploadBusy(true);
    setUploadProgress({ name: file.name, pct: 0 });
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data: up } = await api.post("/admin/uploads/media", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (e) => {
          if (e.total) setUploadProgress({ name: file.name, pct: Math.round((e.loaded * 100) / e.total) });
        },
      });
      if (up.media_type !== "video") {
        throw new Error(isFr ? "Le fichier n'est pas une vidéo." : "File is not a video.");
      }
      const maxOrder = items.reduce((m, x) => Math.max(m, x.order || 0), 0);
      await api.post("/admin/videos", {
        url: up.url,
        kind: "upload",
        on_gallery: true,
        order: maxOrder + 1,
        caption: { fr: "", en: "" },
      });
      await load();
    } catch (e2) {
      setErr(e2?.response?.data?.detail || e2.message || (isFr ? "Erreur d'upload" : "Upload failed"));
    } finally {
      setUploadBusy(false);
      setUploadProgress(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const onDelete = async (vid) => {
    if (!window.confirm(isFr ? "Supprimer définitivement cette vidéo ?" : "Permanently delete this video?")) return;
    await api.delete(`/admin/videos/${vid}`);
    await load();
  };

  const onPatch = async (vid, patch) => {
    await api.patch(`/admin/videos/${vid}`, patch);
    await load();
  };

  return (
    <div className="space-y-8" data-testid="admin-videos-tab">
      {/* Add by URL */}
      <section className="border p-6" style={{ borderColor: "var(--border)", background: "var(--parchment-2)" }}>
        <h3 className="font-display text-xl mb-4">
          {isFr ? "Ajouter une vidéo YouTube ou Vimeo" : "Add a YouTube or Vimeo video"}
        </h3>
        <form onSubmit={onAddEmbed} className="flex flex-wrap gap-2">
          <input
            type="url"
            value={embedUrl}
            onChange={(e) => setEmbedUrl(e.target.value)}
            placeholder={isFr ? "Collez l'URL YouTube ou Vimeo…" : "Paste YouTube or Vimeo URL…"}
            className="input-classic flex-1 min-w-[280px]"
            data-testid="admin-video-embed-url"
            required
          />
          <button type="submit" className="btn btn-primary" disabled={embedBusy}
                  data-testid="admin-video-embed-submit">
            {embedBusy ? "…" : isFr ? "Ajouter" : "Add"}
          </button>
        </form>
      </section>

      {/* Upload file */}
      <section className="border p-6" style={{ borderColor: "var(--border)", background: "white" }}>
        <h3 className="font-display text-xl mb-4">
          {isFr ? "Téléverser un fichier vidéo (mp4, mov, webm…)" : "Upload a video file (mp4, mov, webm…)"}
        </h3>
        <p className="font-body text-sm mb-3" style={{ color: "var(--muted)" }}>
          {isFr ? "Taille max : 200 Mo. Préférez YouTube/Vimeo pour les vidéos > 30 s." : "Max size: 200 MB. Prefer YouTube/Vimeo for videos > 30s."}
        </p>
        <input
          ref={fileInputRef}
          type="file"
          accept="video/*"
          onChange={onUpload}
          disabled={uploadBusy}
          className="font-body text-sm"
          data-testid="admin-video-upload-input"
        />
        {uploadProgress && (
          <div className="mt-3 font-body text-sm" style={{ color: "var(--ink-2)" }}>
            {uploadProgress.name} — {uploadProgress.pct}%
          </div>
        )}
      </section>

      {err && (
        <div className="border-l-4 px-4 py-3 font-body text-sm" data-testid="admin-videos-error"
             style={{ borderColor: "var(--cinnabar)", color: "var(--cinnabar)", background: "rgba(192,76,60,0.06)" }}>
          {err}
        </div>
      )}

      {/* List */}
      <section>
        <h3 className="font-display text-xl mb-4">
          {isFr ? `Vidéos publiées (${items.length})` : `Published videos (${items.length})`}
        </h3>
        {loading ? (
          <p className="font-body text-sm" style={{ color: "var(--muted)" }}>…</p>
        ) : items.length === 0 ? (
          <p className="font-body italic text-sm" style={{ color: "var(--muted)" }}>
            {isFr ? "Aucune vidéo pour l'instant." : "No videos yet."}
          </p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {items.map((v) => (
              <article key={v.id} data-testid={`admin-video-${v.id}`}
                       className="border p-4 flex gap-4" style={{ borderColor: "var(--border)", background: "white" }}>
                <div className="w-40 flex-shrink-0 relative border"
                     style={{ borderColor: "var(--border)", aspectRatio: "16/9", background: "var(--ink)" }}>
                  {v.thumbnail_url ? (
                    <img src={resolveAsset(v.thumbnail_url)} alt=""
                         className="absolute inset-0 w-full h-full object-cover" />
                  ) : (
                    <div className="absolute inset-0 flex items-center justify-center text-white/40 text-xs px-2 text-center">
                      {v.provider}
                    </div>
                  )}
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="w-8 h-8 rounded-full flex items-center justify-center"
                          style={{ background: "rgba(255,255,255,0.9)" }}>
                      <span style={{ marginLeft: 2, color: "var(--ink)" }}>▶</span>
                    </span>
                  </div>
                </div>

                <div className="flex-1 min-w-0">
                  <div className="font-caps text-[0.6rem] mb-1" style={{ color: "var(--gold)" }}>
                    {v.provider?.toUpperCase()} · {v.kind?.toUpperCase()}
                  </div>
                  <a href={v.url} target="_blank" rel="noreferrer"
                     className="block truncate text-xs font-mono" style={{ color: "var(--ink-2)" }}>
                    {v.url}
                  </a>
                  <input
                    type="text"
                    value={v.caption?.fr || ""}
                    onChange={(e) => onPatch(v.id, { caption: { ...(v.caption || {}), fr: e.target.value } })}
                    placeholder={isFr ? "Légende FR…" : "FR caption…"}
                    className="input-classic mt-2 text-sm w-full"
                    data-testid={`admin-video-caption-${v.id}`}
                  />
                  <input
                    type="text"
                    value={v.caption?.en || ""}
                    onChange={(e) => onPatch(v.id, { caption: { ...(v.caption || {}), en: e.target.value } })}
                    placeholder="EN caption…"
                    className="input-classic mt-2 text-sm w-full"
                  />
                  <div className="flex items-center justify-between mt-3">
                    <label className="flex items-center gap-2 font-body text-sm" style={{ color: "var(--ink-2)" }}>
                      <input type="checkbox" checked={!!v.on_gallery}
                             onChange={() => onPatch(v.id, { on_gallery: !v.on_gallery })} />
                      {isFr ? "Affichée" : "Visible"}
                    </label>
                    <button onClick={() => onDelete(v.id)}
                            className="font-caps text-xs px-3 py-1 border"
                            data-testid={`admin-video-delete-${v.id}`}
                            style={{ borderColor: "var(--cinnabar)", color: "var(--cinnabar)" }}>
                      {isFr ? "Supprimer" : "Delete"}
                    </button>
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
